import {
  Box,
  Button,
  Checkbox,
  Fade,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { array, boolean, object, number, string } from 'yup';
// Models
import {
  IAddressBookUserDetails,
  IAddressBookUserUpdate,
  IDropdownResponse
} from '../../../../models';
// Fetch
import { getAddressBookUser, updateAddressBookUser } from '../../../../fetch';
import {
  getAddressBookRelationshipTypes,
  addressBookRelationshipTypesQueryKey
} from '../../../../fetch/email-templates';
// Components
import { Loader, Modal } from '../../../../components';
import { SalesNeighborhoodAutoComplete } from '../../../../components/dropdowns/sales-neighborhood-autocomplete';
import { SalesNotificationAssignmentAutoComplete } from '../../../../components/dropdowns/sales-notification-assignments';
import { SalesTerritoryAutoComplete } from '../../../../components/dropdowns/territory-autocomplete';

interface IEditAddressBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number | null;
  onUpdate: () => void;
}

const validationSchema = object()
  .shape({
    selectedNeighborhoods: array(),
    selectedTerritories: array(),
    isGlobalContact: boolean(),
    notificationTypes: array(),
    addressBookJobTitleId: number().nullable(),
    addressBookJobTitleName: string().nullable()
  })
  .test(
    'neighborhoods-or-territories',
    'At least one neighborhood or territory is required.',
    function (values) {
      const { isGlobalContact, selectedNeighborhoods, selectedTerritories } = values;
      if (
        !isGlobalContact &&
        selectedNeighborhoods.length === 0 &&
        selectedTerritories.length === 0
      ) {
        return false;
      }
      return true;
    }
  );

export const EditAddressBookModal: FC<IEditAddressBookModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUpdate
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState<IAddressBookUserDetails | null>(null);

  const { data: relationshipTypes = [], isLoading: isLoadingRelationshipTypes } = useQuery<
    IDropdownResponse[],
    Error
  >([addressBookRelationshipTypesQueryKey], () => getAddressBookRelationshipTypes());

  useEffect(() => {
    const fetchUserDetails = async (id: number) => {
      setIsLoading(true); // Start loading
      try {
        const data = await getAddressBookUser(id);
        setUserDetails(data);
      } catch (error) {
        enqueueSnackbar('Failed to fetch user details', { variant: 'error' });
      } finally {
        setIsLoading(false); // End loading
      }
    };

    if (userId !== null) {
      fetchUserDetails(userId);
    }
  }, [userId, enqueueSnackbar]);

  const handleSubmit = async (values: any, { setSubmitting, setErrors }: any) => {
    if (userDetails) {
      // Determine if this is a selected option or a custom typed value
      const isCustomValue = !values.addressBookJobTitleId && values.addressBookJobTitleName;
      const selectedRelationshipType = relationshipTypes.find(
        type => type.value === values.addressBookJobTitleId?.toString()
      );

      const updatedUser: IAddressBookUserUpdate = {
        neighborhoodIds: values.selectedNeighborhoods.map((id: string) => parseInt(id)),
        territoryIds: values.selectedTerritories.map((id: string) => parseInt(id)),
        notificationTypes: values.notificationTypes,
        isGlobal: values.isGlobalContact,
        addressBookJobTitleId: values.addressBookJobTitleId || null,
        addressBookJobTitleName: isCustomValue
          ? values.addressBookJobTitleName
          : selectedRelationshipType?.description || ''
      };
      try {
        await updateAddressBookUser(userId, updatedUser);

        // If a custom value was entered, invalidate the relationship types cache
        if (isCustomValue) {
          queryClient.invalidateQueries([addressBookRelationshipTypesQueryKey]);
        }

        onUpdate();
        enqueueSnackbar('User updated successfully', { variant: 'success' });
        onClose();
      } catch (error) {
        const formikErrors: { [field: string]: string } = {};
        if (
          !values.isGlobalContact &&
          values.selectedNeighborhoods.length === 0 &&
          values.selectedTerritories.length === 0
        ) {
          formikErrors.selectedNeighborhoods = 'At least one neighborhood or territory is required';
          formikErrors.selectedTerritories = '';
        }
        setErrors(formikErrors);
        console.error('Update failed:', error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} maxWidth="md" title="Edit Contact">
      <Fade in={isOpen}>
        <Box mt={1}>
          {isLoading || isLoadingRelationshipTypes ? (
            <Grid item xs={12}>
              <Loader position="centered" className="text-center">
                Loading...
              </Loader>
            </Grid>
          ) : (
            <Formik
              initialValues={{
                selectedNeighborhoods: userDetails?.neighborhoodIds.map(id => id.toString()) || [],
                selectedTerritories: userDetails?.territoryIds.map(id => id.toString()) || [],
                isGlobalContact: userDetails?.isGlobal || false,
                notificationTypes: userDetails?.notificationTypes || [],
                addressBookJobTitleId: userDetails?.addressBookJobTitleId || null,
                addressBookJobTitleName:
                  userDetails?.addressBookJobTitleName ||
                  (userDetails?.addressBookJobTitleId
                    ? relationshipTypes.find(
                        type => type.value === userDetails.addressBookJobTitleId?.toString()
                      )?.description || ''
                    : '')
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, handleSubmit, setFieldValue, errors, touched }) => (
                <Form onSubmit={handleSubmit}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        value={userDetails?.displayName}
                        fullWidth
                        size="small"
                        label={userDetails?.displayName ? undefined : 'Contact'}
                        disabled
                        InputProps={{
                          readOnly: true
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={relationshipTypes}
                        getOptionLabel={option =>
                          typeof option === 'string' ? option : option.description
                        }
                        value={
                          values.addressBookJobTitleId
                            ? relationshipTypes.find(
                                type => type.value === values.addressBookJobTitleId?.toString()
                              ) || null
                            : values.addressBookJobTitleName || ''
                        }
                        onChange={(_, newValue: IDropdownResponse | string | null) => {
                          if (typeof newValue === 'string') {
                            // Custom typed value
                            setFieldValue('addressBookJobTitleId', null);
                            setFieldValue('addressBookJobTitleName', newValue);
                          } else if (newValue && typeof newValue === 'object') {
                            // Selected from dropdown
                            setFieldValue('addressBookJobTitleId', parseInt(newValue.value));
                            setFieldValue('addressBookJobTitleName', newValue.description);
                          } else {
                            // Cleared
                            setFieldValue('addressBookJobTitleId', null);
                            setFieldValue('addressBookJobTitleName', '');
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          // Update the display value when typing
                          if (!relationshipTypes.find(type => type.description === newInputValue)) {
                            setFieldValue('addressBookJobTitleName', newInputValue);
                            setFieldValue('addressBookJobTitleId', null);
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Job Title"
                            size="small"
                            fullWidth
                            error={
                              touched.addressBookJobTitleName && !!errors.addressBookJobTitleName
                            }
                            helperText={
                              touched.addressBookJobTitleName && errors.addressBookJobTitleName
                            }
                          />
                        )}
                        disabled={isLoading || isLoadingRelationshipTypes}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SalesNotificationAssignmentAutoComplete
                        limitTags={5}
                        multiple={true}
                        value={values.notificationTypes}
                        handleChange={(val: any[]) => setFieldValue('notificationTypes', val)}
                        handleClear={() => setFieldValue('notificationTypes', [])}
                        disabled={isLoading}
                      />
                      {errors.notificationTypes && touched.notificationTypes && (
                        <FormHelperText error>{errors.notificationTypes}</FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} style={{ padding: '0.25rem 1rem 0 1rem' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.isGlobalContact}
                            onChange={event => {
                              const isChecked = event.target.checked;
                              setFieldValue('isGlobalContact', isChecked);
                              if (isChecked) {
                                setFieldValue('selectedNeighborhoods', []);
                                setFieldValue('selectedTerritories', []);
                              }
                            }}
                            name="isGlobalContact"
                            color="primary"
                          />
                        }
                        label="Include in All Address Books"
                      />
                    </Grid>
                    {!values.isGlobalContact && (
                      <>
                        <Grid item xs={12} style={{ paddingTop: '0.5rem' }}>
                          <SalesTerritoryAutoComplete
                            limitTags={4}
                            multiple={true}
                            value={values.selectedTerritories}
                            handleChange={(val: string[]) =>
                              setFieldValue('selectedTerritories', val)
                            }
                            handleClear={() => setFieldValue('selectedTerritories', [])}
                            disabled={values.isGlobalContact}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <SalesNeighborhoodAutoComplete
                            limitTags={4}
                            value={values.selectedNeighborhoods}
                            handleChange={(val: string[]) =>
                              setFieldValue('selectedNeighborhoods', val)
                            }
                            handleClear={() => setFieldValue('selectedNeighborhoods', [])}
                            isDisabled={values.isGlobalContact}
                            apiParams={{ IsActive: true }}
                          />
                          {errors.selectedNeighborhoods && touched.selectedNeighborhoods && (
                            <FormHelperText error>{errors.selectedNeighborhoods}</FormHelperText>
                          )}
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Button variant="contained" color="primary" type="submit">
                        Update
                      </Button>
                      <Button variant="contained" color="inherit" onClick={onClose}>
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};
